import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type { Banner } from '../components/BannerModalContent';
import BannerModalContent from '../components/BannerModalContent';
import Button from '../components/Button';
import type { ImageEditorRef } from '../components/ImageEditor';
import ImageEditor, {
  DragZoomInfoPill,
  ImageEditorPopOver,
} from '../components/ImageEditor';
import ImageEditorDropZone from '../components/ImageEditorDropZone';
import { POST_BANNER_IMAGE_LIBRARY } from '../components/imageLibraries';
import Sizes from '../utils/responsive/Sizes';
import isSize, { useIsSize } from '../utils/responsive/isSize';
import styles from './PostViewBannerEditor.module.scss';
import postPolicyCheck, { PostPolicyAction } from './postPolicyCheck';
import { EditorActions } from '../editor/editorSlice';
import { PostLayoutBanner, PostLayoutControls } from './PostLayoutContainer';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const POST_FRAGMENT = gql(`
  fragment PostViewBannerEditor_post on Post {
    id
    externalPost {
      externalId
    }
    ...postPolicyCheck_post
  }
`);

const UPDATE_POST_BANNER_MUTATION = gql(`
  mutation updatePostBanner(
    $postId: ID!
    $banner: Upload
    $unsplashId: String
  ) {
    updatePost(id: $postId, banner: $banner, unsplashId: $unsplashId) {
      id
      banner {
        original
      }
    }
  }
`);

interface PostViewBannerEditorProps {
  currentBannerUrl: string | null;
  banner: Banner;
  post: DocumentType<typeof POST_FRAGMENT>;
  setPostLayoutBannerRef: React.Ref<HTMLDivElement>;
}

const PostViewBannerEditor = ({
  currentBannerUrl,
  banner,
  post,
  setPostLayoutBannerRef: postLayoutBannerRef,
}: PostViewBannerEditorProps) => {
  const bannerRef = useRef<HTMLButtonElement>(null);
  const imageEditorRef = useRef<ImageEditorRef>(null);

  const [hasPositionChanged, setHasPositionChanged] = useState(false);
  useEffect(() => {
    setHasPositionChanged(false);
  }, [banner]);

  const dispatch = useAppDispatch();
  const isMobile = useIsSize(Sizes.SM);
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const currentUser = useAppSelector((state) => state.session.user);

  const isPostBannerPopOverOpen = useAppSelector(
    (state) => state.editor.isPostBannerPopOverOpen,
  );
  const isPositioningPostBanner = useAppSelector(
    (state) => state.editor.isPositioningPostBanner,
  );

  const canEditPost = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    currentUser,
  );

  const isDisabled = isMobile || (post.externalPost ? !canEditPost : readOnly);
  const isClickOrUploadDisabled = isDisabled || isPositioningPostBanner;

  const setIsPostBannerPopOverOpen = (open: boolean) => {
    dispatch(EditorActions.setIsPostBannerPopOverOpen(open));
  };

  const setIsPositioningPostBanner = useCallback(
    (isPositioning: boolean) => {
      dispatch(EditorActions.setIsPositioningPostBanner(isPositioning));
    },
    [dispatch],
  );

  const [updatePostBanner] = useMutation(UPDATE_POST_BANNER_MUTATION);

  const uploadBannerFile = useCallback(
    async (file: File) => {
      await updatePostBanner({
        variables: {
          postId: post.id,
          banner: file,
          unsplashId: banner.unsplashId,
        },
      });
    },
    [updatePostBanner, post.id, banner.unsplashId],
  );

  const uploadCurrentBanner = useCallback(async () => {
    const bannerFile = await imageEditorRef.current?.getFile();

    if (bannerFile) {
      await uploadBannerFile(bannerFile);
    }
  }, [uploadBannerFile]);

  useEffect(() => {
    if (hasPositionChanged && readOnly) {
      uploadCurrentBanner();
    }
  }, [readOnly, hasPositionChanged, uploadCurrentBanner]);

  const removeBanner = async () => {
    await updatePostBanner({
      variables: { postId: post.id, banner: null, unsplashId: null },
    });
  };

  const handleBannerChange = async (newBanner: Banner | null) => {
    if (newBanner) {
      setIsPositioningPostBanner(true);
    } else {
      await removeBanner();
      setIsPostBannerPopOverOpen(false);
    }
    dispatch(EditorActions.setCurrentPostBanner(newBanner));
  };

  const handleSavePosition = async () => {
    setIsPositioningPostBanner(false);
    setHasPositionChanged(false);
    await uploadCurrentBanner();
  };

  return (
    <>
      <PostLayoutBanner
        className={classNames(styles.bannerContainer, {
          [styles.isEditing]: !isDisabled,
          [styles.shouldShowControls]:
            isPostBannerPopOverOpen || hasPositionChanged,
        })}
        ref={postLayoutBannerRef}
      >
        <div className={styles.banner}>
          <Button
            onClick={() => setIsPostBannerPopOverOpen(!isPostBannerPopOverOpen)}
            className={classNames(styles.bannerButton, {
              [styles.isPositioning]: isPositioningPostBanner,
            })}
            disabled={isClickOrUploadDisabled}
          >
            <ImageEditorDropZone
              onDropImage={(image) => handleBannerChange({ image })}
              disabled={isClickOrUploadDisabled}
            >
              <ImageEditor
                src={currentBannerUrl}
                value={banner}
                positioning={isPositioningPostBanner}
                ref={imageEditorRef}
                height={380 * 2}
                width={672 * 2}
                onImageLoad={(file) => {
                  if (file) {
                    uploadBannerFile(file);
                  }
                }}
                onScaleChange={() => setHasPositionChanged(true)}
                onPositionChange={() => setHasPositionChanged(true)}
              />
            </ImageEditorDropZone>
          </Button>

          {isPositioningPostBanner && isSize(Sizes.LG) && (
            <div className={styles.dragZoomInfoPillContainer}>
              <DragZoomInfoPill />
            </div>
          )}
        </div>
      </PostLayoutBanner>

      {!isDisabled && (
        <PostLayoutControls className={styles.bannerControlsContainer}>
          <div className={styles.bannerControls}>
            {isPositioningPostBanner && hasPositionChanged && (
              <Button
                variant="secondary"
                onClick={handleSavePosition}
                size="small"
                className={styles.savePositionButton}
              >
                Save position
              </Button>
            )}

            <ImageEditorPopOver
              placement={isSize(Sizes.LG) ? 'bottom' : 'bottom-end'}
              preventCloseOnClickOutside={(target) =>
                bannerRef.current?.contains(target) ?? false
              }
              content={
                <BannerModalContent
                  onRemove={() => handleBannerChange(null)}
                  onBannerChange={handleBannerChange}
                  uploadInfo="16:9 ratio is recommended"
                  imageLibrary={POST_BANNER_IMAGE_LIBRARY}
                  displayMode="grid"
                />
              }
              open={isPostBannerPopOverOpen}
              onOpenChange={setIsPostBannerPopOverOpen}
            >
              <Button
                variant="secondary"
                onClick={() => setIsPostBannerPopOverOpen(true)}
                size="small"
              >
                Edit cover
              </Button>
            </ImageEditorPopOver>
          </div>
        </PostLayoutControls>
      )}
    </>
  );
};

export default PostViewBannerEditor;
